import React from 'react';
import { Tab, TabList, Text, Avatar, Badge, Button } from '@fluentui/react-components';
import {
    Home24Regular,
    Calendar24Regular,
    People24Regular,
    ChartMultiple24Regular,
    Settings24Regular,
    VideoClip24Regular,
    Document24Regular,
    Clock24Regular,
} from '@fluentui/react-icons';
import { useNavigate, useLocation } from 'react-router-dom';

interface UserContext {
    userRole: 'support' | 'doctor' | 'admin';
    userName: string;
    userId: string;
    email: string;
}

interface NavigationProps {
    userContext: UserContext | null;
    onLogout?: () => void;
}

export const Navigation: React.FC<NavigationProps> = ({ userContext, onLogout }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const supportStaffTabs = [
        { key: 'dashboard', label: 'Dashboard', icon: <Home24Regular />, path: '/' },
        {
            key: 'scheduling',
            label: 'Scheduling',
            icon: <Calendar24Regular />,
            path: '/scheduling',
        },
        { key: 'doctors', label: 'Doctors', icon: <People24Regular />, path: '/doctors' },
        { key: 'settings', label: 'Settings', icon: <Settings24Regular />, path: '/settings' },
    ];

    const adminTabs = [
        { key: 'dashboard', label: 'Dashboard', icon: <Home24Regular />, path: '/' },
        {
            key: 'scheduling',
            label: 'Scheduling',
            icon: <Calendar24Regular />,
            path: '/scheduling',
        },
        { key: 'doctors', label: 'Doctors', icon: <People24Regular />, path: '/doctors' },
        {
            key: 'analytics',
            label: 'Analytics',
            icon: <ChartMultiple24Regular />,
            path: '/analytics',
        },
        { key: 'settings', label: 'Settings', icon: <Settings24Regular />, path: '/settings' },
    ];

    const doctorTabs = [
        { key: 'dashboard', label: 'Dashboard', icon: <Home24Regular />, path: '/' },
        { key: 'schedule', label: 'My Schedule', icon: <Calendar24Regular />, path: '/schedule' },
        { key: 'consult', label: 'Active Consult', icon: <VideoClip24Regular />, path: '/consult' },
        { key: 'templates', label: 'Templates', icon: <Document24Regular />, path: '/templates' },
        {
            key: 'availability',
            label: 'Availability',
            icon: <Clock24Regular />,
            path: '/availability',
        },
        { key: 'settings', label: 'Settings', icon: <Settings24Regular />, path: '/settings' },
    ];

    const tabs =
        userContext?.userRole === 'doctor'
            ? doctorTabs
            : userContext?.userRole === 'admin'
            ? adminTabs
            : supportStaffTabs;
    const currentTab = tabs.find((tab) => tab.path === location.pathname)?.key || 'dashboard';

    const handleTabSelect = (tabKey: string) => {
        const tab = tabs.find((t) => t.key === tabKey);
        if (tab) {
            navigate(tab.path);
        }
    };

    return (
        <nav
            style={{
                width: '280px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRight: '1px solid #e1e5e9',
                height: '100vh',
                overflow: 'auto',
            }}
        >
            <div style={{ marginBottom: '32px' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '20px',
                    }}
                >
                    <Text size={700} weight="bold" style={{ color: '#323130' }}>
                        Baylor Psychiatry
                    </Text>
                    <Badge appearance="tint" color="brand">
                        Working!
                    </Badge>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e1e5e9',
                    }}
                >
                    <Avatar name={userContext?.userName || 'User'} size={40} />
                    <div style={{ flex: 1 }}>
                        <Text size={400} weight="semibold" style={{ display: 'block' }}>
                            {userContext?.userName || 'Development User'}
                        </Text>
                        <Text size={300} style={{ color: '#605e5c' }}>
                            {userContext?.userRole === 'doctor'
                                ? 'Child Psychiatrist'
                                : 'Support Staff'}
                        </Text>
                    </div>
                    {onLogout && (
                        <Button
                            appearance="subtle"
                            size="small"
                            onClick={onLogout}
                            style={{ minWidth: 'auto', padding: '4px 8px' }}
                        >
                            Logout
                        </Button>
                    )}
                </div>
            </div>

            <TabList
                selectedValue={currentTab}
                onTabSelect={(_, data) => handleTabSelect(data.value as string)}
                vertical
                style={{ width: '100%' }}
            >
                {tabs.map((tab) => (
                    <Tab
                        key={tab.key}
                        value={tab.key}
                        icon={tab.icon}
                        style={{ justifyContent: 'flex-start', width: '100%', marginBottom: '4px' }}
                    >
                        {tab.label}
                    </Tab>
                ))}
            </TabList>
        </nav>
    );
};
